import { capitalizeFirstLetter } from '@eneco/common/string';
import { DC_Domain_Models_Products_CostDetailType, DC_Domain_Models_Products_ProductType } from '@monorepo-types/dc';
import { GiftData, ProductData as ProductDataEneco, PromotionTexts } from '@sitecore/types/OfferStep';
import {
  CostDetailsExplanation,
  ParagraphField,
  ProductTypeLabels as ProductTypeLabelsEneco,
} from '@sitecore/types/OverviewStep';
import {
  ProductData as ProductDataOxxio,
  ProductTypeLabels as ProductTypeLabelsOxxio,
} from '@sitecore/types/OxxioOverviewStep';

import { ENECO_ENERGY_FLOW_GIFT } from './constants';
import {
  checkIfOfferHasTimeOfUseProduct,
  checkOfferIncludesDynamicPricingProduct,
  checkOfferIncludesHeatingProduct,
  checkOfferIncludesNonDynamicPricingProduct,
  checkOffersIncludeVariablePricingProduct,
  energyTypeIncludes,
} from './misc';
import { FlowConsumerContextEnergy as FlowContext } from '../../context';
import { EnergyType, Offer, OfferContractSpan , OfferType } from '../types';

// Multi Label / Flow Union Types
type ProductDataUnion = Omit<Partial<ProductDataEneco & ProductDataOxxio>, 'productDataList'>;
export type ProductTypeLabelsUnion = Partial<ProductTypeLabelsEneco & ProductTypeLabelsOxxio>;

export const constructTextualDataKey = (contractSpan?: OfferContractSpan[], energyType?: EnergyType): string => {
  const getKeyPrefix = (productType: DC_Domain_Models_Products_ProductType, contractSpans?: OfferContractSpan[]) => {
    const contractSpan = contractSpans?.find(span => span.type === productType);
    // An offer can cover fewer products than the energyType names — a gas-only contract extension
    // for an electricity + gas customer. Drop the segment instead of emitting
    // `undefined-undefined-undefined`, which matches no Sitecore key and blanks the offer card.
    if (!contractSpan) return undefined;

    // Default key pattern
    const textualDataKey = `${contractSpan.duration}-${contractSpan.byPeriod?.toLowerCase()}-${contractSpan.type}`;
    // Prepend priceDifferentiation key to the default key for TimeOfUse products, not to break the current mapping and to handle TimeOfUse properly
    const priceDifferentiationPrefix =
      contractSpan.priceDifferentiation === 'TimeOfUse' ? `${contractSpan.priceDifferentiation}-` : '';
    return `${priceDifferentiationPrefix}${textualDataKey}`;
  };

  const joinKeyPrefixes = (...keyPrefixes: Array<string | undefined>): string => keyPrefixes.filter(Boolean).join('-');

  switch (energyType) {
    case EnergyType.Gas:
      return joinKeyPrefixes(getKeyPrefix('gas', contractSpan));

    case EnergyType.Electricity:
    case EnergyType.ElectricityAndTapWater:
      return joinKeyPrefixes(getKeyPrefix('electricity', contractSpan));

    case EnergyType.ElectricityAndGas:
    case EnergyType.ElectricityAndGasAndWarmth:
    case EnergyType.ElectricityAndGasAndWarmthAndTapWater:
    case EnergyType.ElectricityAndGasAndTapWater:
    case EnergyType.ElectricityAndGasAndWarmthAndTapWaterAndCooling:
      return joinKeyPrefixes(getKeyPrefix('electricity', contractSpan), getKeyPrefix('gas', contractSpan));

    case EnergyType.ElectricityAndWarmth:
    case EnergyType.ElectricityAndWarmthAndTapWater:
    case EnergyType.ElectricityAndWarmthAndCooling:
    case EnergyType.ElectricityAndWarmthAndTapWaterAndCooling:
    case EnergyType.ElectricityAndGeothermalHeating:
      return joinKeyPrefixes(getKeyPrefix('electricity', contractSpan), getKeyPrefix('warmth', contractSpan));

    default:
      return '';
  }
};

export const checkOnDiscountType = (dataKey: string, discountType?: string): string => {
  // DC returns PascalCase values (e.g. ExtendTimeOfUse3Year); Sitecore keys use camelCase suffixes.
  const normalizedDiscountType = discountType?.toLowerCase();

  if (normalizedDiscountType?.includes(OfferType.Variable.toLowerCase())) {
    return `${dataKey}-${OfferType.Variable}`;
  }
  if (normalizedDiscountType?.includes(OfferType.ExtendStayer.toLowerCase())) {
    return `${dataKey}-${OfferType.ExtendStayer}`;
  }
  if (normalizedDiscountType?.includes(OfferType.ExtendTimeOfUse.toLowerCase())) {
    return `${dataKey}-${OfferType.ExtendTimeOfUse}`;
  }
  if (normalizedDiscountType?.includes(OfferType.ExtendRenewalBenefit.toLowerCase())) {
    return `${dataKey}-${OfferType.ExtendRenewalBenefit}`;
  }
  return dataKey;
};

export const getProductTitleFromProductType = (
  productType: DC_Domain_Models_Products_ProductType,
  textualData?: ProductTypeLabelsUnion,
): string => {
  switch (productType) {
    case 'electricity':
      return textualData?.electricityProductLabel?.value.trim() || 'Stroom';

    case 'gas':
      return textualData?.gasProductLabel?.value.trim() || 'Gas';

    case 'warmth':
    case 'warmthEkv':
      return textualData?.warmthProductLabel?.value.trim() || 'Warmte';

    case 'warmtewisselaar':
      return textualData?.warmthExchangeSetLabel?.value.trim() || 'Afleverset';

    case 'tapwater':
      return textualData?.waterProductLabel?.value.trim() || 'Warm water';

    case 'cooling':
      return 'Koude';

    default:
      return capitalizeFirstLetter(productType);
  }
};

type CashbackText = { title: string; text: string; label?: string; trigger: string } | undefined;

export const getCashbackTexts = (
  type?: DC_Domain_Models_Products_CostDetailType,
  promotionTextualData?: PromotionTexts,
  giftDataTextualData?: GiftData,
  cashbackValue?: string | number | undefined,
): CashbackText => {
  if (!type) return undefined;

  if (isCashbackType(type)) {
    return getCashbackPromotionText(type, promotionTextualData);
  }

  if (type === ENECO_ENERGY_FLOW_GIFT) {
    return getGiftPromotionText(giftDataTextualData, cashbackValue);
  }

  return undefined;
};

// check if the type is one of the cashback types
// TODO - replace cashbackDirect and cashbackOnYearNote with constants
const isCashbackType = (type: DC_Domain_Models_Products_CostDetailType): boolean => {
  return type === 'cashBackDirect' || type === 'cashBackOnYearNote';
};

// handle cashback text logic
const getCashbackPromotionText = (
  type: DC_Domain_Models_Products_CostDetailType,
  promotionTextualData?: PromotionTexts,
): CashbackText => {
  const cashbackType = type.replace('cashBack', 'cashback');
  return {
    title: promotionTextualData?.[`${cashbackType}RibbonContent` as keyof PromotionTexts]?.value ?? '',
    text: promotionTextualData?.[`${cashbackType}ExplanationContent` as keyof PromotionTexts]?.value ?? '',
    label: promotionTextualData?.[`${cashbackType}LabelContent` as keyof PromotionTexts]?.value ?? '',
    trigger: promotionTextualData?.cashbackExplanationTriggerText?.value ?? '',
  };
};

// handle gift promotion text logic
const getGiftPromotionText = (
  giftDataTextualData?: GiftData,
  cashbackValue?: string | number | undefined,
): CashbackText => {
  const matchingGift = giftDataTextualData?.giftProductDataList?.find(
    giftProduct => giftProduct?.fields?.gift?.giftId?.value === cashbackValue,
  );

  if (!matchingGift) return undefined;

  return {
    title: matchingGift.fields?.content?.title.value ?? '',
    text: matchingGift.fields?.content?.descriptionContent?.value ?? '',
    trigger: giftDataTextualData?.giftExplanationTriggerText?.value ?? '',
  };
};

const getContractDuration = (offer: Offer, type?: string) => {
  if (type) {
    return offer.contractSpan?.find(contract => contract.type === type);
  }
  return offer?.contractSpan?.[0];
};

/**
 * Returns the contract span with the longest duration across all of an offer's products. Combined
 * offers can bundle a product without its own term (e.g. warmth has `duration: 0`) with one that
 * carries the contract length (e.g. electricity `duration: 1`); the displayed duration should
 * reflect the longest term rather than the zero-duration product.
 */
const getLongestContractDuration = (offer: Offer): OfferContractSpan | undefined =>
  offer?.contractSpan?.reduce((longest, span) => ((span.duration ?? 0) > (longest.duration ?? 0) ? span : longest));

const getContractDurationLabel = (
  periodLabel: string,
  monthlyLabel: string,
  yearLabel: string,
  contractSpan?: OfferContractSpan,
) => {
  const durationLabel = `${contractSpan?.duration} ${contractSpan?.byPeriod === 'M' ? monthlyLabel : yearLabel}`;
  return periodLabel ? `${durationLabel} ${periodLabel}` : durationLabel;
};

export const getOfferTitle = (
  offer: Offer,
  textualData?: ProductDataUnion,
  productTypeLabels?: ProductTypeLabelsUnion,
): string => {
  let contractDuration;
  switch (true) {
    case offer?.discountType?.includes('Hybrid'):
      contractDuration = getContractDuration(offer, 'gas');
      return `${getContractDurationLabel(textualData?.hybridProductNameText?.value.trim() || 'hybride', textualData?.monthsLabel?.value ?? '', textualData?.yearLabel?.value ?? '', contractDuration)}`;
    case offer?.discountType?.includes('dynamic'):
      return textualData?.dynamicProductNameText?.value.trim() || 'dynamisch';
    case offer?.discountType?.includes('variable'):
      return textualData?.variableProductNameText?.value.trim() || 'variabel';
    case offer?.discountType?.includes('TimeOfUse'):
      contractDuration = getContractDuration(offer);
      return `${getContractDurationLabel('vast + Eneco VoordeelMomenten', textualData?.monthsLabel?.value ?? '', textualData?.yearLabel?.value ?? '', contractDuration)}`;
    case checkOfferIncludesHeatingProduct(offer):
      // Warmth products have no contract term of their own (`duration: 0`); use the longest term
      // across the bundled products so a heat + electricity/gas combination shows just the duration,
      // e.g. "1 jaar" (no product label).
      contractDuration = getLongestContractDuration(offer);
      return getContractDurationLabel(
        '',
        textualData?.monthsLabel?.value ?? '',
        textualData?.yearLabel?.value ?? '',
        contractDuration,
      );
    default:
      contractDuration = getContractDuration(offer);
      return `${getContractDurationLabel(textualData?.fixedProductNameText?.value.trim() || 'vast', textualData?.monthsLabel?.value ?? '', textualData?.yearLabel?.value ?? '', contractDuration)}`;
  }
};

export const getOfferSubTitle = (
  energyType: EnergyType,
  textualData?: ProductTypeLabelsUnion,
  discountType?: string,
  variablePeriodLabel?: string,
  fixedPeriodLabel?: string,
): string | undefined => {
  if (
    discountType?.includes('Hybrid') &&
    energyTypeIncludes(energyType, EnergyType.Electricity) &&
    energyTypeIncludes(energyType, EnergyType.Gas)
  ) {
    return `${getProductTitleFromProductType('electricity', textualData)} ${variablePeriodLabel ?? ''} & ${getProductTitleFromProductType(
      'gas',
      textualData,
    )} ${fixedPeriodLabel ?? ''}`;
  } else if (energyTypeIncludes(energyType, EnergyType.Electricity) && energyTypeIncludes(energyType, EnergyType.Gas)) {
    return `${getProductTitleFromProductType('electricity', textualData)} & ${getProductTitleFromProductType(
      'gas',
      textualData,
    )}`;
  } else if (energyTypeIncludes(energyType, EnergyType.Gas)) {
    return getProductTitleFromProductType('gas', textualData);
  } else if (energyTypeIncludes(energyType, EnergyType.Electricity)) {
    return getProductTitleFromProductType('electricity', textualData);
  }
};

export const getPriceDetailsExplanationTexts = (
  flowContext: FlowContext,
  offer?: Offer,
  textualData?: CostDetailsExplanation,
): string => {
  const { hasSolarPanels } = flowContext;
  const isVariableContract = checkOffersIncludeVariablePricingProduct(offer);

  const isDynamicPricingContract =
    checkOfferIncludesDynamicPricingProduct(offer) &&
    !checkOfferIncludesNonDynamicPricingProduct(offer) &&
    !isVariableContract;

  const isHybridContract =
    checkOfferIncludesDynamicPricingProduct(offer) &&
    checkOfferIncludesNonDynamicPricingProduct(offer) &&
    !isVariableContract;

  const isRegularContract = !isDynamicPricingContract && !isHybridContract && !isVariableContract;
  const isTouContract = checkIfOfferHasTimeOfUseProduct(offer);

  return Object.keys(textualData || {})
    .sort(elem =>
      elem === 'lowHighTariffDifferenceParagraph' || elem === 'timeOfUseLowHighTariffDifferenceParagraph' ? -1 : 0,
    )
    .reduce((acc, elem, index) => {
      // exclude simple TextField values
      if (typeof textualData?.[elem as keyof CostDetailsExplanation]?.value === 'string') {
        return acc;
      }

      // exclude redeliveryParagraph or redeliveryDynamicPricingParagraph  based on "hasSolarPanels" value
      if (
        !hasSolarPanels &&
        (elem === 'redeliveryParagraph' ||
          elem === 'redeliveryDynamicPricingParagraph' ||
          elem === 'redeliveryHybridParagraph')
      ) {
        return acc;
      }

      // exclude certain ParagraphField based on a current offer type
      if (isDynamicPricingContract || isHybridContract) {
        if (
          elem === 'monthlyFeesParagraph' ||
          elem === 'redeliveryParagraph' ||
          elem === 'lowHighTariffDifferenceParagraph' ||
          elem === 'timeOfUseLowHighTariffDifferenceParagraph'
        ) {
          return acc;
        }

        if (isDynamicPricingContract) {
          if (
            elem === 'monthlyFeesHybridParagraph' ||
            elem === 'monthlyFeesVariablePricingProductParagraph' ||
            elem === 'redeliveryHybridParagraph'
          ) {
            return acc;
          }
        }

        if (isHybridContract) {
          if (
            elem === 'monthlyFeesDynamicPricingProductParagraph' ||
            elem === 'redeliveryDynamicPricingParagraph' ||
            elem === 'monthlyFeesVariablePricingProductParagraph' ||
            elem === 'timeOfUseLowHighTariffDifferenceParagraph'
          ) {
            return acc;
          }
        }
      }
      if (isVariableContract) {
        if (
          elem === 'monthlyFeesDynamicPricingProductParagraph' ||
          elem === 'redeliveryDynamicPricingParagraph' ||
          elem === 'monthlyFeesHybridParagraph' ||
          elem === 'redeliveryHybridParagraph' ||
          elem === 'timeOfUseLowHighTariffDifferenceParagraph' ||
          elem === 'monthlyFeesParagraph' ||
          elem === 'lowHighTariffDifferenceParagraph' ||
          elem === 'timeOfUseLowHighTariffDifferenceParagraph'
        ) {
          return acc;
        }
      }

      if (isRegularContract) {
        if (
          isTouContract &&
          (elem === 'monthlyFeesDynamicPricingProductParagraph' ||
            elem === 'redeliveryDynamicPricingParagraph' ||
            elem === 'monthlyFeesHybridParagraph' ||
            elem === 'redeliveryHybridParagraph' ||
            elem === 'monthlyFeesVariablePricingProductParagraph' ||
            elem === 'lowHighTariffDifferenceParagraph')
        ) {
          return acc;
        }
        if (
          !isTouContract &&
          (elem === 'monthlyFeesDynamicPricingProductParagraph' ||
            elem === 'redeliveryDynamicPricingParagraph' ||
            elem === 'monthlyFeesHybridParagraph' ||
            elem === 'redeliveryHybridParagraph' ||
            elem === 'monthlyFeesVariablePricingProductParagraph' ||
            elem === 'timeOfUseLowHighTariffDifferenceParagraph')
        ) {
          return acc;
        }
      }

      const paragraph = (textualData?.[elem as keyof CostDetailsExplanation] as ParagraphField)?.value;

      // exclude empty ParagraphField values
      if (!paragraph?.title || !paragraph?.content) {
        return acc;
      }

      // add new paragraph
      acc += `<p><strong>${paragraph?.title}</strong><br/>${paragraph?.content}</p>`;

      // add additional line break if necessary
      acc += `${index + 1 < Object.keys(textualData || {}).length ? '<br/>' : ''}`;

      return acc;
    }, '');
};
